import React, { useState, useRef } from 'react';
import { ArrowLeft, Star, Clock, MapPin, Calendar, Users, Share2, Heart, ShoppingCart, Check, ChevronLeft, ChevronRight, Crown, Tag, Ticket, BookOpen, X, MessageSquare, ThumbsUp, PartyPopper } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { OFFICES, SERVICES, PRODUCTS, COURSES, RENTALS, EVENTS, getOrderById, getBookingById } from '../data/database';
import { useI18n } from '../i18n';

// Custom hook for swipe gestures
const useSwipe = (onSwipeLeft, onSwipeRight) => {
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEnd.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;
    const distance = touchStart.current - touchEnd.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && onSwipeLeft) onSwipeLeft();
    if (isRightSwipe && onSwipeRight) onSwipeRight();
  };

  return { onTouchStart, onTouchMove, onTouchEnd };
};

// Mock reviews data
// Reusable Reviews Section Component - ora legge dal database
const ReviewsSection = ({ officeId, rating = 4.8, reviewCount = 0 }) => {
  const { t } = useI18n();
  const [showAllReviews, setShowAllReviews] = useState(false);
  
  // Import dinamico delle recensioni dal database
  const { getReviewsByOfficeId } = require('../data/database');
  const reviews = officeId ? getReviewsByOfficeId(officeId) : [];
  const actualReviewCount = reviews.length || reviewCount;
  const actualRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) 
    : rating;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 lg:text-lg">{t('detail', 'reviews')}</h3>
        {reviews.length > 2 && (
          <button 
            onClick={() => setShowAllReviews(!showAllReviews)}
            className="text-emerald-600 text-sm font-medium hover:text-emerald-700"
          >
            {showAllReviews ? 'Mostra meno' : 'Vedi tutte'}
          </button>
        )}
      </div>

      {/* Rating Summary */}
      <div className="flex items-center gap-6 mb-6 pb-4 border-b border-gray-100">
        <div className="text-center">
          <div className="text-4xl font-bold text-gray-900">{actualRating}</div>
          <div className="flex items-center gap-0.5 justify-center mt-1">
            {[1,2,3,4,5].map(i => (
              <Star key={i} className={`w-4 h-4 ${i <= Math.round(actualRating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-1">{actualReviewCount} recensioni</p>
        </div>
        <div className="flex-1 space-y-1.5">
          {[5,4,3,2,1].map(stars => {
            const percentage = stars === 5 ? 72 : stars === 4 ? 20 : stars === 3 ? 5 : stars === 2 ? 2 : 1;
            return (
              <div key={stars} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-3">{stars}</span>
                <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full" style={{ width: `${percentage}%` }} />
                </div>
                <span className="text-xs text-gray-500 w-8">{percentage}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.length > 0 ? (showAllReviews ? reviews : reviews.slice(0, 2)).map(review => {
          const reviewDate = new Date(review.date);
          const now = new Date();
          const diffDays = Math.floor((now - reviewDate) / (1000 * 60 * 60 * 24));
          const dateLabel = diffDays === 0 ? 'Oggi' : diffDays === 1 ? 'Ieri' : diffDays < 7 ? `${diffDays} giorni fa` : diffDays < 30 ? `${Math.floor(diffDays / 7)} settimana/e fa` : `${Math.floor(diffDays / 30)} mese/i fa`;
          
          return (
            <div key={review.id} className="pb-4 border-b border-gray-100 last:border-0 last:pb-0">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                  {review.userAvatar ? (
                    <img src={review.userAvatar} alt={review.userName} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-semibold text-emerald-600">{review.userName?.[0] || 'U'}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">{review.userName}</h4>
                    <span className="text-xs text-gray-500">{dateLabel}</span>
                  </div>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-3 h-3 ${i <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{review.text}</p>
                </div>
              </div>
            </div>
          );
        }) : (
          <p className="text-center text-gray-500 py-4">Ancora nessuna recensione</p>
        )}
      </div>

      {/* Write Review Button */}
      <button className="w-full mt-4 py-3 border border-emerald-500 text-emerald-600 rounded-xl font-medium hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2">
        <MessageSquare className="w-4 h-4" />
        Scrivi una recensione
      </button>
    </div>
  );
};

// Service Detail Page
export const ServiceDetailPage = ({ serviceId, onBack, onNavigate }) => {
  const { addToCart, toggleFavorite, isFavorite: checkFavorite } = useApp();
  const { t } = useI18n();
  const service = SERVICES.find(s => s.id === parseInt(serviceId));
  const office = service ? OFFICES.find(b => b.id === service.officeId) : null;
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [currentImage, setCurrentImage] = useState(0);
  const isFavorite = checkFavorite(parseInt(serviceId), 'services');

  // Use office images for service gallery
  const images = service?.images || office?.gallery || [office?.image];

  // Swipe handlers
  const swipeHandlers = useSwipe(
    () => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0), // swipe left = next
    () => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)  // swipe right = prev
  );

  if (!service || !office) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">{t('detail', 'serviceNotFound')}</p>
      </div>
    );
  }

  const handleBook = () => {
    addToCart({ ...service, date: selectedDate, time: selectedTime, businessName: office.name }, 'service');
    onNavigate('cart');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-8">
      {/* Header - Desktop Only */}
      <div className="hidden lg:block bg-white sticky top-[73px] z-20 border-b border-gray-100">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 py-4">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full flex items-center gap-2">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
            <span className="text-gray-700 font-medium">Indietro</span>
          </button>
          <h1 className="font-semibold text-gray-900 lg:text-xl">{t('detail', 'serviceDetail')}</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => toggleFavorite(parseInt(serviceId), 'services')} className="p-2 hover:bg-gray-100 rounded-full">
              <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-400'}`} />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <Share2 className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="max-w-screen-2xl mx-auto px-0 lg:px-6 py-0 lg:py-6">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2">
            {/* Image Gallery with Swipe */}
            <div 
              className="relative bg-white lg:rounded-2xl overflow-hidden mb-0 lg:mb-4"
              {...swipeHandlers}
            >
              <img 
                src={images[currentImage]} 
                alt={service.name} 
                className="w-full h-72 lg:h-96 object-cover select-none" 
                draggable={false}
              />
              {/* Mobile Controls on Image */}
              <button onClick={onBack} className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center lg:hidden z-10">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="absolute top-4 right-4 flex gap-2 z-10 lg:hidden">
                <button onClick={() => toggleFavorite(parseInt(serviceId), 'services')} className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} />
                </button>
                <button className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              {images.length > 1 && (
                <>
                  {/* Navigation Arrows - Same style as BusinessPage */}
                  <button 
                    onClick={() => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button 
                    onClick={() => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  {/* Dots Indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button 
                        key={i} 
                        onClick={() => setCurrentImage(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentImage ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/70'}`} 
                      />
                    ))}
                  </div>
                </>
              )}
              {service.isElit && (
                <div className="absolute top-16 left-4 lg:top-4 flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                  <Crown className="w-3.5 h-3.5" /> ELIT
                </div>
              )}
            </div>

            {/* Service Info */}
            <div className="p-4 lg:p-0">
              <div className="bg-white rounded-none lg:rounded-2xl shadow-sm p-4 lg:p-6 mb-4">
                <span className="text-sm text-emerald-600 font-medium">{service.category}</span>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mt-1">{service.name}</h2>
                
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{service.duration}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span>{office.rating}</span>
                  </div>
                </div>

                {/* Price Box */}
                <div className="mt-6 p-4 bg-emerald-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{t('detail', 'price')}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-emerald-600">€{service.price}</span>
                        {service.originalPrice && (
                          <span className="text-lg text-gray-400 line-through">€{service.originalPrice}</span>
                        )}
                      </div>
                    </div>
                    {service.originalPrice && (
                      <div className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                        -{Math.round((1 - service.price / service.originalPrice) * 100)}%
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">{t('detail', 'description')}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description || `Servizio professionale offerto da ${office.name}. Personale qualificato e attrezzature di ultima generazione per garantirti un'esperienza eccellente.`}
                  </p>
                </div>

                {/* What's included */}
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Cosa include</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {['Consulenza personalizzata', 'Prodotti professionali', 'Garanzia soddisfazione', 'Ambiente sanificato'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-gray-600">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Business Card */}
              <div 
                className="bg-white rounded-2xl shadow-sm p-4 lg:p-6 mb-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onNavigate('office', { id: office.id })}
              >
                <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide">Offerto da</p>
                <div className="flex items-center gap-4">
                  <img src={office.image} alt={office.name} className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg">{office.name}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">{office.address}</p>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        {office.rating}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {office.distance}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Booking - Mobile Only */}
              <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 lg:hidden">
                <h3 className="font-semibold text-gray-900 mb-4">{t('detail', 'book')}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Data</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Ora</label>
                    <select
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 appearance-none"
                    >
                      <option value="">Seleziona orario</option>
                      <option value="09:00">09:00</option>
                      <option value="10:00">10:00</option>
                      <option value="11:00">11:00</option>
                      <option value="14:00">14:00</option>
                      <option value="15:00">15:00</option>
                      <option value="16:00">16:00</option>
                      <option value="17:00">17:00</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Reviews Section */}
              <div className="mb-4">
                <ReviewsSection officeId={office?.id} rating={office?.rating} />
              </div>
            </div>
          </div>

          {/* Sidebar - Desktop Only */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-[130px]">
              <h3 className="font-semibold text-gray-900 text-lg mb-6">{t('detail', 'bookThisService')}</h3>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">Data</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">Ora</label>
                  <select
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all appearance-none"
                  >
                    <option value="">Seleziona orario</option>
                    <option value="09:00">09:00</option>
                    <option value="10:00">10:00</option>
                    <option value="11:00">11:00</option>
                    <option value="14:00">14:00</option>
                    <option value="15:00">15:00</option>
                    <option value="16:00">16:00</option>
                    <option value="17:00">17:00</option>
                  </select>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-4">
                <div className="flex justify-between text-gray-600 mb-2">
                  <span>{t('sections', 'services')}</span>
                  <span>€{service.price}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>{t('booking', 'total')}</span>
                  <span className="text-emerald-600">€{service.price}</span>
                </div>
              </div>

              <button
                onClick={handleBook}
                disabled={!selectedDate || !selectedTime}
                className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('detail', 'bookNow')}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-4">{t('booking', 'freeCancellation')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 lg:hidden">
        <button
          onClick={handleBook}
          disabled={!selectedDate || !selectedTime}
          className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('detail', 'bookNow')} - €{service.price}
        </button>
      </div>
    </div>
  );
};

// Product Detail Page
export const ProductDetailPage = ({ productId, onBack, onNavigate }) => {
  const { addToCart, toggleFavorite, isFavorite: checkFavorite } = useApp();
  const { t } = useI18n();
  const product = PRODUCTS.find(p => p.id === parseInt(productId));
  const office = product ? OFFICES.find(b => b.id === product.officeId) : null;
  const [quantity, setQuantity] = useState(1);
  const [currentImage, setCurrentImage] = useState(0);
  const isFavorite = checkFavorite(parseInt(productId), 'products');

  const images = product?.images || [product?.image];

  // Swipe handlers
  const swipeHandlers = useSwipe(
    () => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0),
    () => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)
  );

  if (!product || !office) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Prodotto non trovato</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-8">
      {/* Header - Desktop Only */}
      <div className="hidden lg:block bg-white sticky top-[73px] z-20 border-b border-gray-100">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 py-4">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full flex items-center gap-2">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
            <span className="text-gray-700 font-medium">Indietro</span>
          </button>
          <h1 className="font-semibold text-gray-900 lg:text-xl">{t('detail', 'productDetail')}</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => toggleFavorite(parseInt(productId), 'products')} className="p-2 hover:bg-gray-100 rounded-full">
              <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-400'}`} />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <Share2 className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="max-w-screen-2xl mx-auto px-0 lg:px-6 py-0 lg:py-6">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2">
            {/* Image Gallery with Swipe */}
            <div 
              className="relative bg-white lg:rounded-2xl overflow-hidden mb-0 lg:mb-4"
              {...swipeHandlers}
            >
              <img 
                src={images[currentImage]} 
                alt={product.name} 
                className="w-full h-80 lg:h-[500px] object-cover select-none" 
                draggable={false}
              />
              {/* Mobile Controls on Image */}
              <button onClick={onBack} className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center lg:hidden z-10">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="absolute top-4 right-4 flex gap-2 lg:hidden z-10">
                <button onClick={() => toggleFavorite(parseInt(productId), 'products')} className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} />
                </button>
                <button className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              {images.length > 1 && (
                <>
                  {/* Navigation Arrows - Same style as BusinessPage */}
                  <button 
                    onClick={() => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button 
                    onClick={() => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  {/* Dots Indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button 
                        key={i}
                        onClick={() => setCurrentImage(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentImage ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/70'}`} 
                      />
                    ))}
                  </div>
                </>
              )}
              {product.discount && (
                <div className="absolute top-16 left-4 lg:top-4 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full shadow-lg">
                  -{product.discount}%
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="p-4 lg:p-0">
              <div className="bg-white rounded-none lg:rounded-2xl shadow-sm p-4 lg:p-6 mb-4">
                <span className="text-sm text-blue-600 font-medium">{product.category}</span>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mt-1">{product.name}</h2>
                
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span>{office.rating}</span>
                  </div>
                  {product.stock && (
                    <span className="text-emerald-600 text-sm font-medium">✓ Disponibile</span>
                  )}
                </div>

                {/* Shipping Info */}
                <div className="mt-4 p-3 rounded-xl bg-gray-50">
                  <div className="flex items-center gap-3">
                    {product.shipping === 'delivery' && (
                      <>
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <ShoppingCart className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Spedizione a domicilio</p>
                          <p className="text-sm text-gray-500">Consegna in tutta Italia</p>
                        </div>
                      </>
                    )}
                    {product.shipping === 'pickup' && (
                      <>
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Solo ritiro in sede</p>
                          <p className="text-sm text-gray-500">Presso {office.name}</p>
                        </div>
                      </>
                    )}
                    {product.shipping === 'both' && (
                      <>
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <ShoppingCart className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Spedizione o Ritiro</p>
                          <p className="text-sm text-gray-500">Scegli tu come riceverlo</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Price Box - Mobile */}
                <div className="mt-6 p-4 bg-blue-50 rounded-xl lg:hidden">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{t('detail', 'price')}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-blue-600">€{product.price}</span>
                        {product.originalPrice && (
                          <span className="text-lg text-gray-400 line-through">€{product.originalPrice}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">{t('detail', 'description')}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {product.description || `Prodotto di alta qualità offerto da ${office.name}. Materiali premium e lavorazione artigianale per garantirti un prodotto eccellente.`}
                  </p>
                </div>

                {/* Features */}
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Caratteristiche</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {['Materiali di qualità', product.shipping !== 'pickup' ? 'Spedizione veloce' : 'Ritiro rapido', 'Reso gratuito', 'Garanzia 2 anni'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-gray-600">
                        <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Business Card */}
              <div 
                className="bg-white rounded-2xl shadow-sm p-4 lg:p-6 mb-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onNavigate('office', { id: office.id })}
              >
                <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide">Venduto da</p>
                <div className="flex items-center gap-4">
                  <img src={office.image} alt={office.name} className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg">{office.name}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">{office.address}</p>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        {office.rating}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {office.distance}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Quantity - Mobile Only */}
              <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 lg:hidden">
                <h3 className="font-semibold text-gray-900 mb-4">Quantità</h3>
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 text-xl font-medium"
                  >
                    -
                  </button>
                  <span className="text-2xl font-semibold w-12 text-center">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(q => q + 1)}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 text-xl font-medium"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Reviews Section */}
              <div className="mb-4">
                <ReviewsSection officeId={office?.id} rating={office?.rating} />
              </div>
            </div>
          </div>

          {/* Sidebar - Desktop Only */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-[130px]">
              <h3 className="font-semibold text-gray-900 text-lg mb-4">{t('detail', 'buyThisProduct')}</h3>
              
              {/* Price */}
              <div className="mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold text-blue-600">€{product.price}</span>
                  {product.originalPrice && (
                    <span className="text-xl text-gray-400 line-through">€{product.originalPrice}</span>
                  )}
                </div>
                {product.discount && (
                  <span className="inline-block mt-2 bg-red-100 text-red-600 text-sm font-medium px-2 py-1 rounded">
                    Risparmi €{(product.originalPrice - product.price).toFixed(2)} ({product.discount}%)
                  </span>
                )}
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <label className="text-sm text-gray-600 mb-2 block">Quantità</label>
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 text-xl font-medium"
                  >
                    -
                  </button>
                  <span className="text-2xl font-semibold w-12 text-center">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(q => q + 1)}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 text-xl font-medium"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-4">
                <div className="flex justify-between text-gray-600 mb-2">
                  <span>Subtotale ({quantity} {quantity > 1 ? 'articoli' : 'articolo'})</span>
                  <span>€{(product.price * quantity).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600 mb-2">
                  <span>Spedizione</span>
                  <span className="text-emerald-600">Gratuita</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-100">
                  <span>Totale</span>
                  <span className="text-blue-600">€{(product.price * quantity).toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  for (let i = 0; i < quantity; i++) {
                    addToCart(product, 'product');
                  }
                  onNavigate('cart');
                }}
                className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                {t('detail', 'addToCart')}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-4">{t('shipping', 'allItaly')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 lg:hidden">
        <button
          onClick={() => {
            for (let i = 0; i < quantity; i++) {
              addToCart(product, 'product');
            }
            onNavigate('cart');
          }}
          className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <ShoppingCart className="w-5 h-5" />
          {t('detail', 'addToCart')} - €{(product.price * quantity).toFixed(2)}
        </button>
      </div>
    </div>
  );
};

// Event Detail Page - modulo eventi non ancora implementato
export const EventDetailPage = ({ eventId, onBack, onNavigate }) => {
  const { addToCart, toggleFavorite, isFavorite: checkFavorite } = useApp();
  const { t } = useI18n();
  
  // Trova l'evento dal database - supporta sia ID stringa che numerico
  const event = EVENTS?.find(e => e.id === eventId || e.id === `evt-${eventId}` || e.id === parseInt(eventId));
  const office = event ? OFFICES.find(o => o.id === event.officeId) : null;
  const [currentImage, setCurrentImage] = useState(0);
  const isFavorite = checkFavorite(eventId, 'events');
  
  const images = event?.images || [event?.image];
  const spotsLeft = event ? event.maxParticipants - event.currentParticipants : 0;
  const isAlmostFull = spotsLeft <= 5;
  const isFree = event?.price === 0;
  
  // Calcola giorni mancanti
  const daysUntil = event ? Math.ceil((new Date(event.date) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  // Swipe handlers
  const swipeHandlers = useSwipe(
    () => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0),
    () => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)
  );

  if (!event || !office) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <PartyPopper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Evento non trovato</p>
          <button onClick={onBack} className="text-emerald-600 font-medium">Torna indietro</button>
        </div>
      </div>
    );
  }

  const handleBookEvent = () => {
    alert(`Prenotazione per "${event.name}" confermata! Ti aspettiamo il ${new Date(event.date).toLocaleDateString('it-IT')}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-8">
      {/* Header - Desktop Only */}
      <div className="hidden lg:block bg-white sticky top-[73px] z-20 border-b border-gray-100">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 py-4">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full flex items-center gap-2">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
            <span className="text-gray-700 font-medium">Indietro</span>
          </button>
          <h1 className="font-semibold text-gray-900 lg:text-xl">Dettaglio Evento</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => toggleFavorite(eventId, 'events')} className="p-2 hover:bg-gray-100 rounded-full">
              <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-400'}`} />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <Share2 className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="max-w-screen-2xl mx-auto px-0 lg:px-6 py-0 lg:py-6">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2">
            {/* Image Gallery with Swipe */}
            <div 
              className="relative bg-white lg:rounded-2xl overflow-hidden mb-0 lg:mb-4"
              {...swipeHandlers}
            >
              <img 
                src={images[currentImage]} 
                alt={event.name} 
                className="w-full h-72 lg:h-[450px] object-cover select-none" 
                draggable={false}
              />
              {/* Mobile Controls on Image */}
              <button onClick={onBack} className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center lg:hidden z-10">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="absolute top-4 right-4 flex gap-2 lg:hidden z-10">
                <button onClick={() => toggleFavorite(eventId, 'events')} className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} />
                </button>
                <button className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              {/* Category Badge */}
              <div className="absolute top-4 left-4 lg:left-4 flex items-center gap-2 lg:flex">
                <span className={`text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5 ${
                  event.category === 'wellness' ? 'bg-purple-500 text-white' : 
                  event.category === 'fitness' ? 'bg-orange-500 text-white' : 
                  event.category === 'workshop' ? 'bg-blue-500 text-white' :
                  'bg-rose-500 text-white'
                }`}>
                  <PartyPopper className="w-3.5 h-3.5" />
                  {event.category}
                </span>
                {isFree && (
                  <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-500 text-white">
                    GRATIS
                  </span>
                )}
              </div>
              {/* Days Until Badge */}
              {daysUntil > 0 && daysUntil <= 30 && (
                <div className="absolute bottom-4 right-4">
                  <span className="text-sm bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full">
                    Tra {daysUntil} giorni
                  </span>
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button 
                    onClick={() => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button 
                    onClick={() => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => setCurrentImage(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${idx === currentImage ? 'bg-white' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Event Info */}
            <div className="bg-white rounded-none lg:rounded-2xl shadow-sm p-4 lg:p-6 mb-4">
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">{event.name}</h1>
              <button 
                onClick={() => onNavigate('office', { id: office.id })}
                className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-4"
              >
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-medium">{office.name}</span>
              </button>
              
              {/* Date & Time */}
              <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-rose-500" />
                  <span className="font-medium">
                    {new Date(event.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-rose-500" />
                  <span className="font-medium">{event.startTime} - {event.endTime}</span>
                </div>
              </div>
              
              {/* Description */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Descrizione</h3>
                <p className="text-gray-600 leading-relaxed">{event.description}</p>
              </div>
              
              {/* Tags */}
              {event.tags && event.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {event.tags.map((tag, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Location */}
            <div className="bg-white rounded-none lg:rounded-2xl shadow-sm p-4 lg:p-6 mb-4">
              <h3 className="font-semibold text-gray-900 mb-3">Luogo</h3>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{office.name}</p>
                  <p className="text-sm text-gray-500">{office.address}</p>
                  {office.phone && <p className="text-sm text-gray-500 mt-1">{office.phone}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Booking Card - Right Column */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-4 lg:p-6 lg:sticky lg:top-32">
              {/* Price */}
              <div className="text-center mb-6 pb-4 border-b border-gray-100">
                {isFree ? (
                  <div className="text-3xl font-bold text-emerald-600">Gratuito</div>
                ) : (
                  <>
                    <div className="text-3xl font-bold text-rose-600">€{event.price}</div>
                    <p className="text-sm text-gray-500">a persona</p>
                  </>
                )}
              </div>
              
              {/* Participants */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Partecipanti</span>
                  <span className="text-sm font-medium">{event.currentParticipants}/{event.maxParticipants}</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${isAlmostFull ? 'bg-red-500' : 'bg-rose-500'}`}
                    style={{ width: `${(event.currentParticipants / event.maxParticipants) * 100}%` }}
                  />
                </div>
                {isAlmostFull && (
                  <p className="text-xs text-red-500 mt-1 font-medium">Solo {spotsLeft} posti rimasti!</p>
                )}
              </div>
              
              {/* CTA Button */}
              <button 
                onClick={handleBookEvent}
                disabled={spotsLeft <= 0}
                className={`w-full py-4 rounded-xl font-semibold text-white transition-colors flex items-center justify-center gap-2 ${
                  spotsLeft <= 0 
                    ? 'bg-gray-300 cursor-not-allowed' 
                    : 'bg-rose-500 hover:bg-rose-600'
                }`}
              >
                <Ticket className="w-5 h-5" />
                {spotsLeft <= 0 ? 'Evento al completo' : isFree ? 'Prenota il tuo posto' : 'Acquista biglietto'}
              </button>
              
              {/* Info */}
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span>Conferma immediata</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span>Cancellazione gratuita fino a 48h prima</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40">
        <div className="flex items-center justify-between gap-4">
          <div>
            {isFree ? (
              <span className="text-xl font-bold text-emerald-600">Gratuito</span>
            ) : (
              <>
                <span className="text-xl font-bold text-rose-600">€{event.price}</span>
                <span className="text-gray-500 text-sm">/persona</span>
              </>
            )}
            <p className="text-xs text-gray-500">{spotsLeft} posti rimasti</p>
          </div>
          <button 
            onClick={handleBookEvent}
            disabled={spotsLeft <= 0}
            className={`flex-1 py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 ${
              spotsLeft <= 0 ? 'bg-gray-300' : 'bg-rose-500'
            }`}
          >
            <Ticket className="w-5 h-5" />
            {spotsLeft <= 0 ? 'Esaurito' : 'Prenota'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Course Detail Page
export const CourseDetailPage = ({ courseId, onBack, onNavigate }) => {
  const { addToCart, toggleFavorite, isFavorite: checkFavorite } = useApp();
  const { t } = useI18n();
  const course = COURSES?.find(c => c.id === parseInt(courseId));
  const office = course ? OFFICES.find(b => b.id === course.officeId) : null;
  const [currentImage, setCurrentImage] = useState(0);
  const isFavorite = checkFavorite(parseInt(courseId), 'courses');

  const images = course?.images || [course?.image];
  const spotsLeft = course ? course.maxParticipants - course.currentParticipants : 0;

  // Swipe handlers
  const swipeHandlers = useSwipe(
    () => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0),
    () => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)
  );

  if (!course || !office) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Corso non trovato</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-8">
      {/* Header - Desktop Only */}
      <div className="hidden lg:block bg-white sticky top-[73px] z-20 border-b border-gray-100">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 py-4">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full flex items-center gap-2">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
            <span className="text-gray-700 font-medium">Indietro</span>
          </button>
          <h1 className="font-semibold text-gray-900 lg:text-xl">{t('detail', 'courseDetail')}</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => toggleFavorite(parseInt(courseId), 'courses')} className="p-2 hover:bg-gray-100 rounded-full">
              <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-400'}`} />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <Share2 className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="max-w-screen-2xl mx-auto px-0 lg:px-6 py-0 lg:py-6">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2">
            {/* Image Gallery with Swipe */}
            <div 
              className="relative bg-white lg:rounded-2xl overflow-hidden mb-0 lg:mb-4"
              {...swipeHandlers}
            >
              <img 
                src={images[currentImage]} 
                alt={course.name} 
                className="w-full h-72 lg:h-[450px] object-cover select-none" 
                draggable={false}
              />
              {/* Mobile Controls on Image */}
              <button onClick={onBack} className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center lg:hidden z-10">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="absolute top-4 right-4 flex gap-2 lg:hidden z-10">
                <button onClick={() => toggleFavorite(parseInt(courseId), 'courses')} className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} />
                </button>
                <button className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              {images.length > 1 && (
                <>
                  {/* Navigation Arrows - Same style as BusinessPage */}
                  <button 
                    onClick={() => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button 
                    onClick={() => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  {/* Dots Indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button 
                        key={i}
                        onClick={() => setCurrentImage(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentImage ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/70'}`} 
                      />
                    ))}
                  </div>
                </>
              )}
              <div className="absolute top-16 left-4 lg:top-4 flex gap-2">
                <span className="bg-indigo-500 text-white text-sm font-bold px-3 py-1 rounded-full shadow-lg">{course.category}</span>
                <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-sm font-medium px-3 py-1 rounded-full shadow-lg">{course.level}</span>
              </div>
            </div>

            {/* Course Info */}
            <div className="p-4 lg:p-0">
              <div className="bg-white rounded-none lg:rounded-2xl shadow-sm p-4 lg:p-6 mb-4">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{course.name}</h2>
                
                {/* Course Stats */}
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-indigo-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-600">{course.totalHours}</p>
                    <p className="text-xs text-gray-500">ore totali</p>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-600">{course.lessons}</p>
                    <p className="text-xs text-gray-500">lezioni</p>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-600">{spotsLeft}</p>
                    <p className="text-xs text-gray-500">posti disponibili</p>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-600">4.9</p>
                    <p className="text-xs text-gray-500">valutazione</p>
                  </div>
                </div>

                {/* Schedule & Location */}
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Inizio corso</p>
                      <p className="text-sm text-gray-500">{new Date(course.startDate).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{t('detail', 'schedule')}</p>
                      <p className="text-sm text-gray-500">{course.schedule}</p>
                    </div>
                  </div>
                  
                  {/* Location - Different for in-person vs online */}
                  {course.locationType === 'online' ? (
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <div className="relative">
                          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Corso Online</p>
                        <p className="text-sm text-gray-500">Lezioni in diretta via Zoom/Meet</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{course.location}</p>
                        <p className="text-sm text-gray-500">{course.city} • Vedi su mappa</p>
                      </div>
                      {course.distance && (
                        <div className="bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {course.distance}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Price Box - Mobile */}
                <div className="mt-6 p-4 bg-indigo-50 rounded-xl lg:hidden">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Costo corso</p>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-indigo-600">€{course.price}</span>
                        {course.originalPrice && (
                          <span className="text-lg text-gray-400 line-through">€{course.originalPrice}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">{t('detail', 'description')}</h3>
                  <p className="text-gray-600 leading-relaxed">{course.description || `Un corso completo per imparare ${course.name}. Docenti esperti e materiale didattico incluso.`}</p>
                </div>

                {/* What's included */}
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Il corso include</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(course.includes || ['Materiale didattico', 'Attestato di partecipazione', 'Supporto docente', 'Accesso alla community']).map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-gray-600">
                        <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Requirements */}
                {course.requirements && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Requisiti</h3>
                    <div className="space-y-2">
                      {course.requirements.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-gray-600">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Organizer Card */}
              <div 
                className="bg-white rounded-2xl shadow-sm p-4 lg:p-6 mb-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onNavigate('office', { id: office.id })}
              >
                <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide">Organizzato da</p>
                <div className="flex items-center gap-4">
                  <img src={office.image} alt={office.name} className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg">{office.name}</h3>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        {office.rating}
                      </span>
                      <span>•</span>
                      <span>{office.distance}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Reviews Section */}
              <div className="mb-4">
                <ReviewsSection officeId={course?.officeId} />
              </div>
            </div>
          </div>

          {/* Sidebar - Desktop Only */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-[130px]">
              <h3 className="font-semibold text-gray-900 text-lg mb-4">{t('detail', 'enrollInCourse')}</h3>
              
              {/* Price */}
              <div className="mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold text-indigo-600">€{course.price}</span>
                  {course.originalPrice && (
                    <span className="text-xl text-gray-400 line-through">€{course.originalPrice}</span>
                  )}
                </div>
                {course.originalPrice && (
                  <span className="inline-block mt-2 bg-red-100 text-red-600 text-sm font-medium px-2 py-1 rounded">
                    Risparmi €{(course.originalPrice - course.price).toFixed(2)}
                  </span>
                )}
              </div>

              {/* Quick Info */}
              <div className="space-y-3 mb-6 pb-6 border-b border-gray-100">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Durata</span>
                  <span className="font-medium text-gray-900">{course.totalHours} ore</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Lezioni</span>
                  <span className="font-medium text-gray-900">{course.lessons}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Posti disponibili</span>
                  <span className="font-medium text-gray-900">{spotsLeft}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Livello</span>
                  <span className="font-medium text-gray-900">{course.level}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  addToCart({ ...course, businessName: office.name }, 'course');
                  onNavigate('cart');
                }}
                disabled={spotsLeft === 0}
                className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <BookOpen className="w-5 h-5" />
                {spotsLeft > 0 ? t('detail', 'enrollNow') : t('detail', 'courseFull')}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-4">Posti limitati • Pagamento sicuro</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 lg:hidden">
        <button
          onClick={() => {
            addToCart({ ...course, businessName: office.name }, 'course');
            onNavigate('cart');
          }}
          disabled={spotsLeft === 0}
          className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <BookOpen className="w-5 h-5" />
          {spotsLeft > 0 ? `${t('detail', 'enroll')} - €${course.price}` : t('detail', 'courseFull')}
        </button>
      </div>
    </div>
  );
};

// Order Detail Page
export const OrderDetailPage = ({ orderId, onBack, onNavigate }) => {
  const { t } = useI18n();
  const { addToCart } = useApp();
  const [showContactModal, setShowContactModal] = useState(false);
  
  // Get order from centralized database
  const order = getOrderById(orderId);
  
  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Ordine non trovato</p>
          <button onClick={onBack} className="mt-4 text-emerald-600 font-medium">Torna indietro</button>
        </div>
      </div>
    );
  }

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    preparing: 'bg-purple-100 text-purple-700',
    ready: 'bg-indigo-100 text-indigo-700',
    shipped: 'bg-blue-100 text-blue-700',
    delivered: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  const statusLabels = {
    pending: 'In attesa',
    confirmed: 'Confermato',
    preparing: 'In preparazione',
    ready: 'Pronto',
    shipped: 'Spedito',
    delivered: 'Consegnato',
    completed: 'Completato',
    cancelled: 'Annullato'
  };

  const getTimelineSteps = () => {
    const steps = [
      { key: 'created', label: 'Ordine effettuato', date: order.createdAt },
    ];
    
    if (order.status === 'confirmed' || ['preparing', 'ready', 'shipped', 'delivered', 'completed'].includes(order.status)) {
      steps.push({ key: 'confirmed', label: 'Confermato', date: order.confirmedAt || order.createdAt });
    }
    if (['preparing', 'ready', 'shipped', 'delivered', 'completed'].includes(order.status)) {
      steps.push({ key: 'preparing', label: 'In preparazione', date: order.preparingAt });
    }
    if (['ready', 'shipped', 'delivered', 'completed'].includes(order.status)) {
      steps.push({ key: 'ready', label: order.fulfillmentType === 'delivery' ? 'In consegna' : 'Pronto per ritiro', date: order.readyAt });
    }
    if (['delivered', 'completed'].includes(order.status)) {
      steps.push({ key: 'delivered', label: 'Consegnato', date: order.deliveredAt || order.date });
    }
    
    return steps;
  };

  const handleReorder = () => {
    // Add all items to cart
    order.items?.forEach(item => {
      const product = PRODUCTS.find(p => p.id === item.productId);
      if (product) {
        for (let i = 0; i < (item.qty || 1); i++) {
          addToCart(product, 'product');
        }
      }
    });
    onNavigate('cart');
  };

  const subtotal = order.subtotal || order.items?.reduce((sum, item) => sum + (item.price || 0) * (item.qty || 1), 0) || 0;
  const shippingCost = order.shippingCost || 0;
  const total = order.total || (subtotal + shippingCost);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white sticky top-0 z-20 border-b border-gray-100 lg:top-[73px]">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 py-4">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="font-semibold text-gray-900">{t('detail', 'orderDetail')}</h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-0 lg:px-6 py-0 lg:py-6 space-y-4">
        {/* Order Status Card */}
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Ordine #{order.id}</p>
              <p className="text-xs text-gray-400 mt-1">
                {order.createdAt ? new Date(order.createdAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : order.date}
              </p>
            </div>
            <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-700'}`}>
              {statusLabels[order.status] || order.status}
            </span>
          </div>
          
          {/* Timeline */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                {getTimelineSteps().map((step, i) => (
                  <React.Fragment key={step.key}>
                    <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                    {i < getTimelineSteps().length - 1 && <div className="w-0.5 h-12 bg-emerald-200" />}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex-1 space-y-8">
                {getTimelineSteps().map((step) => (
                  <div key={step.key}>
                    <p className="font-medium text-gray-900">{step.label}</p>
                    <p className="text-sm text-gray-500">
                      {step.date ? new Date(step.date).toLocaleString('it-IT') : '-'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Business */}
        {order.office && (
          <div 
            className="bg-white rounded-2xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onNavigate('office', { id: order.office.id })}
          >
            <p className="text-xs text-gray-500 mb-3">VENDUTO DA</p>
            <div className="flex items-center gap-3">
              <img src={order.office.image} alt={order.office.name} className="w-12 h-12 rounded-xl object-cover" />
              <div className="flex-1">
                <p className="font-medium text-gray-900">{order.office.name}</p>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); setShowContactModal(true); }}
                className="text-emerald-600 text-sm font-medium hover:text-emerald-700"
              >
                Contatta
              </button>
            </div>
          </div>
        )}

        {/* Items */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-3">ARTICOLI ({order.items?.length || 0})</p>
          <div className="space-y-3">
            {order.items?.map((item, idx) => (
              <div key={item.id || idx} className="flex items-center gap-3">
                {item.image && (
                  <img src={item.image} alt={item.name} className="w-16 h-16 rounded-xl object-cover" />
                )}
                {!item.image && (
                  <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{item.name}</p>
                  <p className="text-sm text-gray-500">Qtà: {item.qty || item.quantity || 1}</p>
                </div>
                <p className="font-semibold text-gray-900">€{((item.price || 0) * (item.qty || item.quantity || 1)).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Shipping/Pickup Info */}
        {(order.shipping?.address || order.fulfillmentType) && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-3">
              {order.fulfillmentType === 'pickup' || order.fulfillmentType === 'takeaway' ? 'RITIRO' : 'SPEDIZIONE'}
            </p>
            <div className="space-y-2">
              {order.shipping?.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <p className="text-gray-700">{order.shipping.address}</p>
                </div>
              )}
              {order.fulfillmentType === 'pickup' && order.pickupTime && (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <p className="text-gray-700">Ritiro alle {order.pickupTime}</p>
                </div>
              )}
              {order.estimatedDelivery && (
                <p className="text-sm text-emerald-600 ml-8">Consegna stimata: {order.estimatedDelivery}</p>
              )}
            </div>
          </div>
        )}

        {/* Payment Summary */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-3">RIEPILOGO PAGAMENTO</p>
          <div className="space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Subtotale</span>
              <span>€{subtotal.toFixed(2)}</span>
            </div>
            {shippingCost > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Spedizione</span>
                <span>€{shippingCost.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-gray-100 pt-2 flex justify-between font-semibold text-gray-900">
              <span>Totale</span>
              <span>€{total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button 
            onClick={handleReorder}
            className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Riordina
          </button>
          <button className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors">
            Lascia recensione
          </button>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && order.office && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center" onClick={() => setShowContactModal(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Contatta {order.office.name}</h3>
              <button onClick={() => setShowContactModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <a 
                href={`tel:${order.office.phone}`}
                className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors"
              >
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Chiama</p>
                  <p className="text-sm text-gray-500">{order.office.phone}</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Booking Detail Page
export const BookingDetailPage = ({ bookingId, onBack, onNavigate }) => {
  const { t } = useI18n();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  // Get booking from centralized database
  const booking = getBookingById(bookingId);
  
  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Prenotazione non trovata</p>
          <button onClick={onBack} className="mt-4 text-emerald-600 font-medium">Torna indietro</button>
        </div>
      </div>
    );
  }

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  const statusLabels = {
    pending: 'In attesa',
    confirmed: 'Confermata',
    completed: 'Completata',
    cancelled: 'Annullata'
  };

  // Determine booking date
  const bookingDateStr = booking.date || booking.checkIn || booking.startDate;
  const bookingDate = bookingDateStr ? new Date(bookingDateStr) : new Date();
  const isUpcoming = bookingDate >= new Date();

  // Determine what type of booking this is (service, hotel, restaurant, rental)
  const isHotelBooking = booking.roomType || booking.roomName || booking.checkIn;
  const isRestaurantBooking = booking.tableName || booking.tableId;
  const isRentalBooking = booking.itemName || booking.rentalItem;

  // Calculate total for multi-day bookings
  const calculateTotal = () => {
    if (booking.totalPrice) return booking.totalPrice;
    if (booking.price) return booking.price;
    if (booking.pricePerNight && booking.nights) return booking.pricePerNight * booking.nights;
    if (booking.pricePerDay && booking.days) return booking.pricePerDay * booking.days;
    if (booking.service?.price) return booking.service.price;
    return 0;
  };

  const totalPrice = calculateTotal();

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white sticky top-0 z-20 border-b border-gray-100 lg:top-[73px]">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 py-4">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="font-semibold text-gray-900">{t('detail', 'bookingDetail')}</h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-0 lg:px-6 py-0 lg:py-6 space-y-4">
        {/* Status Card */}
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Prenotazione #{booking.id}</p>
              {booking.isElit && (
                <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full mt-1">
                  <Crown className="w-3 h-3" /> ELIT
                </span>
              )}
            </div>
            <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${statusColors[booking.status] || 'bg-gray-100 text-gray-700'}`}>
              {statusLabels[booking.status] || booking.status}
            </span>
          </div>

          {/* Date & Time Highlight */}
          <div className="bg-emerald-50 rounded-xl p-4 text-center">
            <p className="text-emerald-600 font-medium">
              {bookingDate.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            {booking.time && (
              <p className="text-3xl font-bold text-emerald-700 mt-1">{booking.time}</p>
            )}
            {isHotelBooking && booking.checkOut && (
              <p className="text-sm text-emerald-600 mt-2">
                Check-out: {new Date(booking.checkOut).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
              </p>
            )}
          </div>
        </div>

        {/* Service/Room/Table Info */}
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm">
          <p className="text-xs text-gray-500 mb-3">
            {isHotelBooking ? 'CAMERA' : isRestaurantBooking ? 'TAVOLO' : isRentalBooking ? 'NOLEGGIO' : 'SERVIZIO'}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium text-gray-900 text-lg">
                {booking.serviceName || booking.service?.name || booking.roomName || booking.tableName || booking.itemName || 'Prenotazione'}
              </p>
              {booking.roomType && <p className="text-sm text-gray-500">{booking.roomType}</p>}
              <div className="flex items-center gap-3 mt-2 text-sm text-gray-500 flex-wrap">
                {booking.duration && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {booking.duration} {typeof booking.duration === 'number' ? 'min' : ''}
                  </span>
                )}
                {booking.service?.duration && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {booking.service.duration}
                  </span>
                )}
                {booking.guests && (
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {booking.guests} {booking.guests > 1 ? 'ospiti' : 'ospite'}
                  </span>
                )}
                {booking.nights && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {booking.nights} {booking.nights > 1 ? 'notti' : 'notte'}
                  </span>
                )}
                {booking.days && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {booking.days} {booking.days > 1 ? 'giorni' : 'giorno'}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-600">€{totalPrice}</p>
              {booking.pricePerNight && (
                <p className="text-xs text-gray-500">€{booking.pricePerNight}/notte</p>
              )}
              {booking.pricePerDay && (
                <p className="text-xs text-gray-500">€{booking.pricePerDay}/giorno</p>
              )}
            </div>
          </div>
          
          {/* Operator/Professional */}
          {(booking.operatorName || booking.professional) && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="font-medium text-emerald-600">{(booking.operatorName || booking.professional)?.charAt(0)}</span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Professionista</p>
                <p className="font-medium text-gray-900">{booking.operatorName || booking.professional}</p>
              </div>
            </div>
          )}
        </div>

        {/* Business */}
        {booking.office && (
          <div 
            className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onNavigate('office', { id: booking.office.id })}
          >
            <p className="text-xs text-gray-500 mb-3">PRESSO</p>
            <div className="flex items-center gap-3">
              <img src={booking.office.image} alt={booking.office.name} className="w-14 h-14 rounded-xl object-cover" />
              <div className="flex-1">
                <p className="font-medium text-gray-900">{booking.office.name}</p>
                <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{typeof booking.office.address === 'string' ? booking.office.address : booking.office.address?.full || 'Indirizzo'}</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        )}

        {/* Notes */}
        {booking.notes && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-2">NOTE</p>
            <p className="text-gray-700">{booking.notes}</p>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-3">
          <a 
            href={`tel:${booking.office?.phone || '+39 081 123 4567'}`}
            className="bg-white rounded-xl p-4 shadow-sm flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <span className="text-xs font-medium text-gray-700">Chiama</span>
          </a>
          <button className="bg-white rounded-xl p-4 shadow-sm flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-xs font-medium text-gray-700">Calendario</span>
          </button>
        </div>

        {/* Actions */}
        {isUpcoming && booking.status !== 'cancelled' && (
          <div className="flex gap-3">
            <button className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
              Modifica
            </button>
            <button 
              onClick={() => setShowCancelModal(true)}
              className="flex-1 py-3 bg-red-50 text-red-600 rounded-xl font-medium hover:bg-red-100 transition-colors"
            >
              Annulla
            </button>
          </div>
        )}

        {!isUpcoming && booking.status !== 'cancelled' && (
          <button className="w-full py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors">
            Prenota di nuovo
          </button>
        )}
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center" onClick={() => setShowContactModal(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Contatta {booking.office?.name || 'Attività'}</h3>
              <button onClick={() => setShowContactModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <a 
                href={`tel:${booking.office?.phone || '+39 081 123 4567'}`}
                className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors"
              >
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Chiama</p>
                  <p className="text-sm text-gray-500">{booking.office?.phone || '+39 081 123 4567'}</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center" onClick={() => setShowCancelModal(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Annulla prenotazione?</h3>
              <p className="text-gray-500 mt-2">Sei sicuro di voler annullare questa prenotazione? L'azione non può essere annullata.</p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Mantieni
              </button>
              <button 
                onClick={() => {
                  setShowCancelModal(false);
                  // In a real app, this would call an API to cancel
                  onBack();
                }}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors"
              >
                Annulla prenotazione
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// RENTAL DETAIL PAGE - Dettaglio Noleggio/Soggiorno (Prenotazioni Giornaliere)
// ============================================================================
const RentalDetailPage = ({ rentalId, onBack, onNavigate }) => {
  const { toggleFavorite, isFavorite: checkFavorite } = useApp();
  const { t } = useI18n();
  const [currentImage, setCurrentImage] = useState(0);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [showBookingConfirm, setShowBookingConfirm] = useState(false);

  const rental = RENTALS.find(r => r.id === parseInt(rentalId)) || RENTALS[0];
  const office = OFFICES.find(o => o.id === rental?.officeId);
  const isFavorite = checkFavorite(parseInt(rentalId), 'rentals');
  
  const images = rental?.images || [
    'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800',
  ];

  const swipeHandlers = useSwipe(
    () => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0),
    () => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)
  );

  const price = rental?.pricePerNight || rental?.pricePerDay || 0;
  const priceLabel = rental?.pricePerNight ? 'notte' : 'giorno';
  const minStay = rental?.minStay || rental?.minDays || 1;
  const maxStay = rental?.maxStay || rental?.maxDays || 30;

  // Calculate total if dates selected
  const calculateTotal = () => {
    if (!checkIn || !checkOut) return null;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (nights < minStay) return null;
    return nights * price;
  };

  const total = calculateTotal();
  const nights = checkIn && checkOut ? Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)) : 0;

  if (!rental) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Offerta non trovata</p>
          <button onClick={onBack} className="mt-4 text-blue-600">Torna indietro</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32 lg:pb-8">
      {/* Desktop Header */}
      <div className="hidden lg:flex items-center justify-between bg-white border-b px-6 py-4 sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
            <span>Indietro</span>
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <h1 className="font-semibold text-gray-900">{rental.name}</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => toggleFavorite(parseInt(rentalId), 'rentals')} className="p-2 rounded-lg hover:bg-gray-100">
            <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} />
          </button>
          <button className="p-2 rounded-lg hover:bg-gray-100">
            <Share2 className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto lg:px-6 lg:py-6">
        <div className="lg:grid lg:grid-cols-5 lg:gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-3">
            {/* Image Gallery */}
            <div 
              className="relative h-72 sm:h-80 lg:h-[450px] lg:rounded-2xl overflow-hidden"
              {...swipeHandlers}
            >
              <img 
                src={images[currentImage]} 
                alt={rental.name} 
                className="w-full h-full object-cover"
              />
              {/* Mobile Controls */}
              <button onClick={onBack} className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center lg:hidden z-10">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="absolute top-4 right-4 flex gap-2 lg:hidden z-10">
                <button onClick={() => toggleFavorite(parseInt(rentalId), 'rentals')} className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} />
                </button>
              </div>
              {images.length > 1 && (
                <>
                  <button 
                    onClick={() => setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button 
                    onClick={() => setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button 
                        key={i}
                        onClick={() => setCurrentImage(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentImage ? 'bg-white w-6' : 'bg-white/50'}`} 
                      />
                    ))}
                  </div>
                </>
              )}
              {/* Category Badge */}
              <div className="absolute top-16 lg:top-4 left-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                {rental.category}
              </div>
            </div>

            {/* Rental Info */}
            <div className="p-4 lg:p-0 lg:mt-6">
              <div className="bg-white rounded-2xl shadow-sm p-4 lg:p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{rental.name}</h2>
                    <button 
                      onClick={() => onNavigate('office', { id: office?.id })}
                      className="text-blue-600 hover:text-blue-700 font-medium mt-1 flex items-center gap-1"
                    >
                      {office?.name} <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">€{price}</p>
                    <p className="text-gray-500 text-sm">/{priceLabel}</p>
                  </div>
                </div>

                {rental.description && (
                  <p className="text-gray-600 mt-4">{rental.description}</p>
                )}

                {/* Key Details */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-4 border-t border-gray-100">
                  {(rental.maxGuests || rental.maxPassengers) && (
                    <div className="text-center p-3 bg-gray-50 rounded-xl">
                      <Users className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                      <p className="text-sm font-medium text-gray-900">Max {rental.maxGuests || rental.maxPassengers}</p>
                      <p className="text-xs text-gray-500">{rental.maxGuests ? 'ospiti' : 'passeggeri'}</p>
                    </div>
                  )}
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <Calendar className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                    <p className="text-sm font-medium text-gray-900">Min {minStay}</p>
                    <p className="text-xs text-gray-500">{rental.pricePerNight ? 'notti' : 'giorni'}</p>
                  </div>
                  {rental.sqm && (
                    <div className="text-center p-3 bg-gray-50 rounded-xl">
                      <MapPin className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                      <p className="text-sm font-medium text-gray-900">{rental.sqm} m²</p>
                      <p className="text-xs text-gray-500">superficie</p>
                    </div>
                  )}
                </div>

                {/* Amenities/Features */}
                {(rental.amenities || rental.features) && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <h3 className="font-semibold text-gray-900 mb-3">{rental.amenities ? 'Servizi inclusi' : 'Caratteristiche'}</h3>
                    <div className="flex flex-wrap gap-2">
                      {(rental.amenities || rental.features).map((item, i) => (
                        <span key={i} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm">
                          <Check className="w-4 h-4 inline mr-1" />
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Reviews */}
              <div className="mt-4">
                <ReviewsSection officeId={office?.id} rating={office?.rating} />
              </div>
            </div>
          </div>

          {/* Right Column - Booking Widget (Desktop) */}
          <div className="hidden lg:block lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <div className="text-center mb-6">
                <p className="text-3xl font-bold text-blue-600">€{price}</p>
                <p className="text-gray-500">/{priceLabel}</p>
              </div>

              {/* Date Selection */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {rental.pricePerNight ? 'Check-in' : 'Dal'}
                    </label>
                    <input
                      type="date"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {rental.pricePerNight ? 'Check-out' : 'Al'}
                    </label>
                    <input
                      type="date"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                      min={checkIn || new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>

                {(rental.maxGuests || rental.maxPassengers) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {rental.maxGuests ? 'Ospiti' : 'Passeggeri'}
                    </label>
                    <select
                      value={guests}
                      onChange={(e) => setGuests(parseInt(e.target.value))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      {[...Array(rental.maxGuests || rental.maxPassengers)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1} {(rental.maxGuests ? 'ospite' : 'passeggero')}{i > 0 ? 'i' : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              {total && (
                <div className="mt-6 pt-4 border-t border-gray-100 space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>€{price} x {nights} {rental.pricePerNight ? 'notti' : 'giorni'}</span>
                    <span>€{total}</span>
                  </div>
                  <div className="flex justify-between font-bold text-gray-900 pt-2 border-t">
                    <span>Totale</span>
                    <span>€{total}</span>
                  </div>
                </div>
              )}

              {nights > 0 && nights < minStay && (
                <p className="text-red-500 text-sm mt-3">Soggiorno minimo: {minStay} {rental.pricePerNight ? 'notti' : 'giorni'}</p>
              )}

              <button
                onClick={() => setShowBookingConfirm(true)}
                disabled={!total || nights < minStay}
                className="w-full mt-6 py-3.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Prenota ora
              </button>

              <p className="text-center text-sm text-gray-500 mt-3">
                Non ti verrà addebitato nulla ora
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 lg:hidden z-30">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xl font-bold text-blue-600">€{price}<span className="text-sm font-normal text-gray-500">/{priceLabel}</span></p>
            {total && <p className="text-sm text-gray-500">Totale: €{total}</p>}
          </div>
          <button
            onClick={() => setShowBookingConfirm(true)}
            className="px-8 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors"
          >
            Prenota
          </button>
        </div>
      </div>

      {/* Booking Confirmation Modal */}
      {showBookingConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center" onClick={() => setShowBookingConfirm(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Conferma prenotazione</h3>
            
            <div className="space-y-4">
              {/* Mobile Date Selection */}
              <div className="lg:hidden grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-in</label>
                  <input
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-out</label>
                  <input
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    min={checkIn}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl"
                  />
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <img src={images[0]} alt={rental.name} className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <p className="font-semibold">{rental.name}</p>
                    <p className="text-sm text-gray-500">{office?.name}</p>
                  </div>
                </div>
                {total && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between font-bold">
                      <span>Totale</span>
                      <span className="text-blue-600">€{total}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setShowBookingConfirm(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium"
              >
                Annulla
              </button>
              <button 
                onClick={() => {
                  setShowBookingConfirm(false);
                  // Navigate to checkout or show success
                  alert('Prenotazione confermata! (Demo)');
                }}
                disabled={!total || nights < minStay}
                className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-medium disabled:bg-gray-300"
              >
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { RentalDetailPage };